import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Paper,
  Typography,
  Stepper,
  Step,
  StepLabel,
  Alert
} from "@mui/material";
import { useNavigate } from "react-router-dom";

const steps = ["Account Information", "Admin User"];

export default function SignupPage() {
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = useState(0);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  // Step 1: Account information
  const [accountName, setAccountName] = useState("");
  const [accountAddress, setAccountAddress] = useState("");

  // Step 2: Admin user information
  const [userName, setUserName] = useState("");
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");

  const handleNext = () => {
    setError("");

    // Validate step 1
    if (activeStep === 0) {
      if (!accountName.trim()) {
        setError("Account name is required");
        return;
      }
      setActiveStep(1);
    }
  };

  const handleBack = () => {
    setError("");
    setActiveStep(0);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    // Validate step 2
    if (!userName.trim()) {
      setError("Name is required");
      return;
    }
    if (!username.trim()) {
      setError("Username is required");
      return;
    }
    if (!email.trim()) {
      setError("Email is required");
      return;
    }
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      setError("Please enter a valid email address");
      return;
    }
    if (!password) {
      setError("Password is required");
      return;
    }
    if (password.length < 6) {
      setError("Password must be at least 6 characters");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL || "http://localhost:8000"}/signup/`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            account_name: accountName,
            account_address: accountAddress.trim() || null,
            user_name: userName,
            username: username,
            email: email,
            password: password
          })
        }
      );

      const data = await response.json();

      if (response.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || "Signup failed. Please try again.");
      }
    } catch (err) {
      setError("Network error. Please try again.");
    }
  };

  if (success) {
    return (
      <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
        <Paper sx={{ p: 4, maxWidth: 600, width: "100%" }}>
          <Alert severity="success" sx={{ mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              Signup Successful!
            </Typography>
            <Typography>
              Your account has been created and is pending activation by an administrator.
              You will be able to login once your account is activated.
            </Typography>
          </Alert>
          <Button
            variant="contained"
            fullWidth
            onClick={() => navigate("/login")}
          >
            Go to Login
          </Button>
        </Paper>
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", p: 2 }}>
      <Paper sx={{ p: 4, maxWidth: 600, width: "100%" }}>
        <Typography variant="h4" gutterBottom align="center">
          Create Account
        </Typography>
        <Typography variant="body2" color="text.secondary" align="center" sx={{ mb: 3 }}>
          Sign up for POPPIS - Development Application Intelligence
        </Typography>

        <Stepper activeStep={activeStep} sx={{ mb: 4 }}>
          {steps.map((label) => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {activeStep === 0 && (
          <Box>
            <Typography variant="h6" gutterBottom>
              Account Information
            </Typography>
            <TextField
              label="Account Name"
              value={accountName}
              onChange={(e) => setAccountName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              required
              helperText="Your company or organization name"
            />
            <TextField
              label="Account Address (Optional)"
              value={accountAddress}
              onChange={(e) => setAccountAddress(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
              multiline
              rows={3}
              helperText="Your business address"
            />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button onClick={() => navigate("/login")}>
                Back to Login
              </Button>
              <Button variant="contained" onClick={handleNext}>
                Next
              </Button>
            </Box>
          </Box>
        )}

        {activeStep === 1 && (
          <form onSubmit={handleSubmit}>
            <Typography variant="h6" gutterBottom>
              Admin User
            </Typography>
            <TextField
              label="Full Name"
              value={userName}
              onChange={(e) => setUserName(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              required
            />
            <TextField
              label="Username"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              required
              helperText="Choose a unique username"
            />
            <TextField
              label="Email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              required
              helperText="This will be your account email"
            />
            <TextField
              label="Password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              fullWidth
              sx={{ mb: 2 }}
              required
              helperText="Minimum 6 characters"
            />
            <TextField
              label="Confirm Password"
              type="password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              fullWidth
              sx={{ mb: 3 }}
              required
            />
            <Box sx={{ display: "flex", justifyContent: "space-between" }}>
              <Button onClick={handleBack}>
                Back
              </Button>
              <Button type="submit" variant="contained">
                Create Account
              </Button>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
}
